import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Card, Modal, FormField, FormSelect, TextInput, EmptyState, StatusBadge, Toggle, PageHeader } from '@/components/ui';
import { SectionToolbar } from './telephony/SectionToolbar';
import { Icon } from '@/components/ui/Icon';
import { adminApi, type IvrPromptRow } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { PromptField, PromptsManager } from './ivr/PromptField';

type NodeType = 'menu' | 'play' | 'queue' | 'voicemail' | 'transfer';

interface IvrProps {
  entry?: boolean;
  audio?: string;
  intro?: string;
  prompt_id?: number | null;
  timeout?: number;
  target?: string;
  mailbox?: string;
}

interface IvrNode {
  id: string;
  type: NodeType;
  label: string;
  x: number;
  y: number;
  props?: IvrProps;
}

interface IvrEdge {
  from: string;
  to: string;
  digit?: string;
  label?: string;
}

interface IvrGraph {
  nodes: IvrNode[];
  edges: IvrEdge[];
}

interface IvrMenu {
  id: number;
  name: string;
  intro_audio?: string;
  graph?: IvrGraph;
  active: boolean;
}

interface Target {
  value: string;
  label: string;
}

const TYPE_HE: Record<NodeType, string> = {
  menu: 'תפריט',
  play: 'הודעה',
  queue: 'תור',
  voicemail: 'תא קולי',
  transfer: 'העברה',
};

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '*', '#'];
// 'timeout' = יעד "לא הוקשה בחירה" (אחרי 3 השמעות של התפריט).
const ROUTE_KEYS = [...DIGITS, 'timeout'];

const TYPE_ICON: Record<NodeType, string> = {
  menu: 'keypad', play: 'play', queue: 'users', voicemail: 'voicemail', transfer: 'transfer',
};

// רוחב קבוע לצומת + גובה עוגן לנקודות החיבור (מרכז הכותרת).
const NODE_W = 232;
const ANCHOR_Y = 42;

/** צומת תפריט/הודעה בלי הקלטה = מצב אזהרה (הבורר בדיאלפלן ייכשל בשקט). */
function nodeWarning(n: IvrNode): string | null {
  if ((n.type === 'menu' || n.type === 'play') && !n.props?.prompt_id && !n.props?.audio) {
    return 'שגיאה: חסרה הקלטת אודיו';
  }
  return null;
}

/** שורת הסטטוס התחתונה של צומת תקין — תקציר הניתוב שלו. */
function nodeSummary(n: IvrNode, edges: IvrEdge[], nodes: IvrNode[]): string {
  if (n.type === 'menu') {
    const routes = edges.filter((e) => e.from === n.id && e.digit).length;
    return routes > 0 ? `${routes} יעדי הקשה מנותבים` : 'טרם נותבו הקשות';
  }
  if (n.type === 'play') {
    const next = edges.find((e) => e.from === n.id && !e.digit);
    const to = next && nodes.find((x) => x.id === next.to);
    return to ? `ממשיך אל: ${to.label}` : 'ללא המשך (ניתוק)';
  }
  if (n.type === 'queue' || n.type === 'transfer') return n.props?.target ? `יעד: ${n.props.target}` : 'לא נבחר יעד';
  if (n.type === 'voicemail') return n.props?.mailbox ? `תיבה: ${n.props.mailbox}` : 'תיבת ברירת מחדל';
  return '';
}

export function Ivr({ tabs }: { tabs?: ReactNode } = {}) {
  const [menus, setMenus] = useState<IvrMenu[]>([]);
  const [active, setActive] = useState<IvrMenu | null>(null);
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  // אילו מספרים (DIDs) מנותבים לכל תפריט — לתגית "מחובר למספר".
  const [didsByMenu, setDidsByMenu] = useState<Record<string, string[]>>({});

  useEffect(() => { void load(); }, []);

  async function load() {
    const res = await adminApi.ivr();
    setMenus(res.data as IvrMenu[]);
    try {
      const dids = await adminApi.dids();
      const map: Record<string, string[]> = {};
      for (const d of (dids.data as { number: string; route_type?: string; route_target?: string }[]) ?? []) {
        if (d.route_type === 'ivr' && d.route_target) {
          (map[d.route_target] ??= []).push(d.number);
        }
      }
      setDidsByMenu(map);
    } catch { /* לא קריטי לתצוגה */ }
  }

  async function create() {
    if (!name) return;
    try {
      await adminApi.createIvr({ name, graph: { nodes: [], edges: [] } });
      toast('נוצר', 'ok');
      setShow(false);
      setName('');
      void load();
    } catch {
      toast('שגיאה ביצירת תפריט', 'danger');
    }
  }

  async function remove(m: IvrMenu) {
    try {
      await adminApi.deleteIvr(m.id);
      toast('התפריט נמחק', 'ok');
      setMenus((prev) => prev.filter((x) => x.id !== m.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  if (active) {
    // העורך תופס את כל המסך; הלשוניות נשארות מעליו כדי שאפשר יהיה לעבור.
    return (
      <>
        {tabs && <div style={{ padding: 'var(--gap) var(--gap) 0' }}>{tabs}</div>}
        <IvrEditor menu={active} onClose={() => { setActive(null); void load(); }} />
      </>
    );
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <SectionToolbar tabs={tabs} title="תפריטי IVR" actions={<button type="button" className="btn primary" onClick={() => setShow(true)}>+ תפריט חדש</button>} />

      {menus.length === 0 ? (
        <Card><EmptyState icon="tree" title="עדיין אין תפריטי IVR" description="צור תפריט ראשון כדי להתחיל." /></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))', gap: 'var(--gap)' }}>
          {menus.map((m, i) => {
            const numbers = didsByMenu[String(m.id)] ?? [];
            return (
              // כניסה מדורגת (0.06 שנ' בין כרטיסים) וריחוף שמרים את הכרטיס — כמו אפשרויות התפריט בעיצוב.
              <Card key={m.id} className="hover-lift" style={{ ['--d' as string]: `${i * 0.06}s` }}>
                {/* כרטיס תפריט בסגנון העיצוב: אריח אייקון כהה, שם · מספר, ושורת תקציר. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <span aria-hidden style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, display: 'grid', placeItems: 'center', background: 'var(--text)', color: 'var(--bg)' }}>
                    <Icon name="keypad" size={18} />
                  </span>
                  <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ fontSize: 'var(--t-lg)', fontWeight: 700 }}>{m.name}</strong>
                      <StatusBadge tone={m.active ? 'ok' : 'neutral'} size="sm" dot>{m.active ? 'פעיל' : 'כבוי'}</StatusBadge>
                    </div>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 3 }}>
                      {m.graph?.nodes?.length ?? 0} שלבים
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginInlineStart: 'auto' }}>
                    <button type="button" className="btn" onClick={() => setActive(m)}>
                      <Icon name="edit" size={14} /> ערוך
                    </button>
                    <button type="button" className="btn danger" onClick={() => remove(m)} aria-label={`מחק את ${m.name}`}>
                      <Icon name="trash" size={14} />
                    </button>
                  </div>
                </div>
                <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {numbers.length > 0 ? (
                    <span style={{ fontSize: 'var(--t-xs)', padding: '5px 10px', borderRadius: 'var(--r-pill)', background: 'var(--surface-3)', color: 'var(--text-2)' }}>
                      מספרים המשויכים לתפריט: <span dir="ltr">{numbers.join(', ')}</span>
                    </span>
                  ) : (
                    <span style={{ fontSize: 'var(--t-xs)', padding: '5px 10px', borderRadius: 'var(--r-pill)', background: 'var(--warn-soft)', color: 'var(--warn)' }}>
                      לא מחובר למספר — חבר במסך "מספרים"
                    </span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal isOpen={show} onClose={() => setShow(false)} title="תפריט IVR חדש"
        footer={<><button type="button" className="btn" onClick={() => setShow(false)}>ביטול</button><button type="button" className="btn primary" onClick={create}>שמור</button></>}>
        <FormField label="שם" required><TextInput value={name} onChange={(e) => setName(e.target.value)} /></FormField>
      </Modal>
    </div>
  );
}

function IvrEditor({ menu, onClose }: { menu: IvrMenu; onClose: () => void }) {
  const [graph, setGraph] = useState<IvrGraph>(menu.graph ?? { nodes: [], edges: [] });
  const [introAudio, setIntroAudio] = useState(menu.intro_audio ?? '');
  const [activeFlag, setActiveFlag] = useState(menu.active);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [targets, setTargets] = useState<{ queues: Target[]; extensions: Target[] }>({ queues: [], extensions: [] });
  const [prompts, setPrompts] = useState<IvrPromptRow[]>([]);
  const [managePrompts, setManagePrompts] = useState(false);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ id: string; offX: number; offY: number; moved: boolean } | null>(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    void adminApi.ivrTargets().then(setTargets);
    void adminApi.ivrPrompts().then(setPrompts).catch(() => {});
  }, []);

  const selected = graph.nodes.find((n) => n.id === selectedId) ?? null;

  function mutate(fn: (g: IvrGraph) => IvrGraph) {
    setGraph((g) => fn(g));
    setDirty(true);
  }

  function addNode(type: NodeType) {
    const id = `n${Date.now()}`;
    const node: IvrNode = {
      id,
      type,
      label: TYPE_HE[type],
      x: 60 + (graph.nodes.length % 5) * 180,
      y: 60 + Math.floor(graph.nodes.length / 5) * 130,
      props: graph.nodes.length === 0 ? { entry: true } : {},
    };
    mutate((g) => ({ ...g, nodes: [...g.nodes, node] }));
    setSelectedId(id);
  }

  function removeNode(id: string) {
    mutate((g) => ({
      nodes: g.nodes.filter((n) => n.id !== id),
      edges: g.edges.filter((e) => e.from !== id && e.to !== id),
    }));
    if (selectedId === id) setSelectedId(null);
  }

  function updateNode(id: string, patch: Partial<IvrNode>) {
    mutate((g) => ({ ...g, nodes: g.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) }));
  }

  function updateProps(id: string, patch: Partial<IvrProps>) {
    mutate((g) => ({
      ...g,
      nodes: g.nodes.map((n) => (n.id === id ? { ...n, props: { ...n.props, ...patch } } : n)),
    }));
  }

  function setEntry(id: string) {
    mutate((g) => ({
      ...g,
      nodes: g.nodes.map((n) => ({ ...n, props: { ...n.props, entry: n.id === id } })),
    }));
  }

  // Set/replace the edge for a (menu node, digit) → target node.
  function routeDigit(fromId: string, digit: string, toId: string | null) {
    mutate((g) => {
      const edges = g.edges.filter((e) => !(e.from === fromId && e.digit === digit));
      if (toId) edges.push({ from: fromId, to: toId, digit });
      return { ...g, edges };
    });
  }

  // Single onward edge for non-menu nodes (play → next).
  function setNext(fromId: string, toId: string | null) {
    mutate((g) => {
      const edges = g.edges.filter((e) => !(e.from === fromId && !e.digit));
      if (toId) edges.push({ from: fromId, to: toId });
      return { ...g, edges };
    });
  }

  function onPointerDown(e: React.PointerEvent, n: IvrNode) {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    // הקואורדינטות של הצמתים הן במרחב הגרף — מחלקים בזום.
    drag.current = { id: n.id, offX: (e.clientX - rect.left) / zoom - n.x, offY: (e.clientY - rect.top) / zoom - n.y, moved: false };
    setSelectedId(n.id);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, (e.clientX - rect.left) / zoom - drag.current.offX);
    const y = Math.max(0, (e.clientY - rect.top) / zoom - drag.current.offY);
    const id = drag.current.id;
    drag.current.moved = true;
    setGraph((g) => ({ ...g, nodes: g.nodes.map((nn) => (nn.id === id ? { ...nn, x, y } : nn)) }));
  }

  function onPointerUp() {
    if (drag.current?.moved) setDirty(true);
    drag.current = null;
  }

  function validate(): string | null {
    if (graph.nodes.length === 0) return null;
    const entry = graph.nodes.find((n) => n.props?.entry);
    if (!entry) return 'יש לסמן צומת כניסה אחד.';
    for (const n of graph.nodes) {
      if ((n.type === 'queue' || n.type === 'transfer') && !n.props?.target) {
        return `לצומת "${n.label}" חסר יעד.`;
      }
    }
    return null;
  }

  async function save() {
    const err = validate();
    if (err) { toast(err, 'danger'); return; }
    setSaving(true);
    try {
      await adminApi.saveIvr(menu.id, graph, { intro_audio: introAudio || null, active: activeFlag });
      toast('נשמר', 'ok');
      setDirty(false);
      onClose();
    } catch {
      toast('שגיאה בשמירה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title={menu.name} actions={<><div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Toggle checked={activeFlag} onChange={(v) => { setActiveFlag(v); setDirty(true); }} label="פעיל" />
          <button type="button" className="btn" onClick={onClose}>חזור</button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'שומר…' : dirty ? 'שמור שינויים' : 'שמור'}
          </button>
        </div></>} />

      <p style={{ margin: 0, color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>
        הודעת הפתיחה היא ההקלטה של צומת הכניסה (▶). לכל שלב אפשר לצרף הקלטה —
        העלאה או יצירה מטקסט — דרך "מאפייני הצומת".
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 300px', gap: 'var(--gap)', alignItems: 'start' }}>
        <Card title="הוסף צומת">
          <div className="stack" style={{ gap: 6 }}>
            {(Object.keys(TYPE_HE) as NodeType[]).map((t) => (
              <button key={t} type="button" className="btn" onClick={() => addNode(t)}>+ {TYPE_HE[t]}</button>
            ))}
          </div>
        </Card>

        <Card padded={false}>
          <style>{`
            @keyframes ivr-flow { from { stroke-dashoffset: 24; } to { stroke-dashoffset: 0; } }
            .ivr-node { transition: box-shadow .25s, transform .25s, border-color .25s; }
            .ivr-node:hover { transform: translateY(-2px); box-shadow: var(--shadow-3); }
            .ivr-node .ivr-del { opacity: 0; transition: opacity .15s, color .15s, background .15s; }
            .ivr-node:hover .ivr-del { opacity: 1; }
            @keyframes ivr-pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }
          `}</style>
          <div
            ref={canvasRef}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            style={{
              position: 'relative', minHeight: 560, overflow: 'hidden',
              borderRadius: 'var(--r-3)',
              backgroundImage: 'radial-gradient(color-mix(in oklch, var(--muted) 45%, transparent) 1px, transparent 1px)',
              backgroundSize: '24px 24px', backgroundPosition: '-12px -12px', touchAction: 'none',
            }}
          >
            {/* שכבת הגרף — הזום חל עליה בלבד, כך שקואורדינטות הצמתים נשארות במרחב הגרף */}
            <div style={{ position: 'absolute', inset: 0, transform: `scale(${zoom})`, transformOrigin: '0 0' }}>
              {/* קשתות bezier מונפשות עם תווית הקשה */}
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                {graph.edges.map((e, i) => {
                  const from = graph.nodes.find((n) => n.id === e.from);
                  const to = graph.nodes.find((n) => n.id === e.to);
                  if (!from || !to) return null;
                  // עוגנים בצידי הצמתים לפי הכיוון היחסי ביניהם.
                  const leftToRight = to.x >= from.x;
                  const x1 = leftToRight ? from.x + NODE_W : from.x;
                  const x2 = leftToRight ? to.x : to.x + NODE_W;
                  const y1 = from.y + ANCHOR_Y, y2 = to.y + ANCHOR_Y;
                  const bend = Math.min(90, Math.max(45, Math.abs(x2 - x1) / 2)) * (leftToRight ? 1 : -1);
                  const d = `M ${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`;
                  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
                  return (
                    <g key={i}>
                      {/* צל רך מתחת לקו */}
                      <path d={d} stroke="var(--line)" strokeWidth={4} fill="none" transform="translate(0,3)" opacity={0.35} />
                      {/* הקו הזורם */}
                      <path
                        d={d} stroke="var(--accent)" strokeWidth={2.5} fill="none"
                        strokeDasharray="6 6" style={{ animation: 'ivr-flow 1s linear infinite' }} opacity={0.9}
                      />
                      {e.digit && (
                        <>
                          <circle cx={mx} cy={my} r={12} fill="var(--surface)" stroke="var(--accent)" strokeWidth={2} />
                          <text x={mx} y={my + 4} textAnchor="middle" fontSize={11} fill="var(--accent)" fontWeight={700}>
                            {e.digit === 'timeout' ? '⏱' : e.digit}
                          </text>
                        </>
                      )}
                    </g>
                  );
                })}
              </svg>

              {graph.nodes.map((n) => {
                const isSel = n.id === selectedId;
                const warning = nodeWarning(n);
                return (
                  <div
                    key={n.id}
                    className="ivr-node"
                    onPointerDown={(e) => onPointerDown(e, n)}
                    style={{
                      position: 'absolute', left: n.x, top: n.y, width: NODE_W,
                      background: 'color-mix(in oklch, var(--surface) 92%, transparent)',
                      backdropFilter: 'blur(4px)',
                      border: `2px solid ${isSel
                        ? 'var(--accent)'
                        : warning
                          ? 'color-mix(in oklch, var(--danger) 45%, var(--line))'
                          : 'var(--line)'}`,
                      borderRadius: 16,
                      boxShadow: isSel ? '0 0 0 4px color-mix(in oklch, var(--accent) 22%, transparent)' : 'var(--shadow-1)',
                      cursor: 'grab', userSelect: 'none', zIndex: isSel ? 3 : 2,
                    }}
                  >
                    {/* תגית נקודת התחלה */}
                    {n.props?.entry && (
                      <div style={{
                        position: 'absolute', top: -11, insetInlineStart: 14, zIndex: 4,
                        background: 'var(--text)', color: 'var(--surface)',
                        fontSize: 10, fontWeight: 700, padding: '2px 10px',
                        borderRadius: 'var(--r-pill)', boxShadow: 'var(--shadow-2)',
                      }}>
                        נקודת התחלה
                      </div>
                    )}
                    {/* נקודות חיבור דקורטיביות בצדדים */}
                    {(['-7px', undefined] as const).map((left, side) => (
                      <div key={side} style={{
                        position: 'absolute', top: ANCHOR_Y - 7, width: 14, height: 14,
                        left, right: left === undefined ? -7 : undefined,
                        background: 'var(--surface)', border: '2px solid var(--line)',
                        borderRadius: '50%', zIndex: 4,
                      }} />
                    ))}

                    <div style={{ padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                          <div style={{
                            padding: 9, borderRadius: 12, flexShrink: 0,
                            background: warning ? 'var(--danger-soft)' : 'var(--accent-soft)',
                            color: warning ? 'var(--danger)' : 'var(--accent-ink)',
                            display: 'flex',
                          }}>
                            <Icon name={TYPE_ICON[n.type]} size={18} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 'var(--t-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.label}</div>
                            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 500 }}>{TYPE_HE[n.type]}</div>
                          </div>
                        </div>
                        <button
                          type="button" className="ivr-del"
                          onPointerDown={(e) => e.stopPropagation()} onClick={() => removeNode(n.id)}
                          aria-label="מחק צומת"
                          style={{
                            border: 'none', background: 'none', color: 'var(--muted)',
                            cursor: 'pointer', padding: 6, borderRadius: 8, lineHeight: 0, flexShrink: 0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'var(--danger-soft)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--muted)'; e.currentTarget.style.background = 'none'; }}
                        >
                          <Icon name="trash" size={14} />
                        </button>
                      </div>

                      {/* פס סטטוס תחתון: אזהרה או תקציר ניתוב */}
                      {warning ? (
                        <div style={{
                          marginTop: 10, display: 'flex', alignItems: 'center', gap: 8,
                          fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--danger)',
                          background: 'color-mix(in oklch, var(--danger-soft) 60%, transparent)',
                          border: '1px solid color-mix(in oklch, var(--danger) 25%, transparent)',
                          borderRadius: 10, padding: '8px 10px',
                        }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--danger)', animation: 'ivr-pulse 1.2s ease-in-out infinite', flexShrink: 0 }} />
                          {warning}
                        </div>
                      ) : (
                        <div style={{
                          marginTop: 10, fontSize: 'var(--t-xs)', fontWeight: 500, color: 'var(--text-2)',
                          background: 'color-mix(in oklch, var(--surface-2) 60%, transparent)',
                          border: '1px solid var(--line)', borderRadius: 10, padding: '8px 10px',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {nodeSummary(n, graph.edges, graph.nodes)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {graph.nodes.length === 0 && (
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'var(--muted)' }}>
                הוסף צומת מהפאנל. בחר צומת כדי לערוך מאפיינים וניתוב.
              </div>
            )}

            {/* סרגל זום צף */}
            <div style={{
              position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)',
              display: 'flex', alignItems: 'center', gap: 2, zIndex: 5,
              background: 'var(--surface)', border: '1px solid var(--line)',
              borderRadius: 'var(--r-pill)', boxShadow: 'var(--shadow-2)', padding: '4px 6px',
            }}>
              <button type="button" className="btn ghost" aria-label="הקטן" style={{ width: 28, height: 28, padding: 0, borderRadius: '50%' }}
                onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))}>−</button>
              <button type="button" className="btn ghost" title="אפס זום" style={{ fontSize: 'var(--t-xs)', fontWeight: 700, minWidth: 46, height: 28, padding: '0 6px' }}
                onClick={() => setZoom(1)} dir="ltr">{Math.round(zoom * 100)}%</button>
              <button type="button" className="btn ghost" aria-label="הגדל" style={{ width: 28, height: 28, padding: 0, borderRadius: '50%' }}
                onClick={() => setZoom((z) => Math.min(1.5, Math.round((z + 0.1) * 10) / 10))}>+</button>
            </div>
          </div>
        </Card>

        <NodeInspector
          node={selected}
          graph={graph}
          targets={targets}
          prompts={prompts}
          onManagePrompts={() => setManagePrompts(true)}
          onLabel={(label) => selected && updateNode(selected.id, { label })}
          onProp={(patch) => selected && updateProps(selected.id, patch)}
          onSetEntry={() => selected && setEntry(selected.id)}
          onRouteDigit={(digit, toId) => selected && routeDigit(selected.id, digit, toId)}
          onSetNext={(toId) => selected && setNext(selected.id, toId)}
        />
      </div>

      {managePrompts && (
        <PromptsManager
          prompts={prompts}
          onClose={() => setManagePrompts(false)}
          onChanged={() => void adminApi.ivrPrompts().then(setPrompts)}
        />
      )}
    </div>
  );
}

function NodeInspector({
  node, graph, targets, prompts, onManagePrompts, onLabel, onProp, onSetEntry, onRouteDigit, onSetNext,
}: {
  node: IvrNode | null;
  graph: IvrGraph;
  targets: { queues: Target[]; extensions: Target[] };
  prompts: IvrPromptRow[];
  onManagePrompts: () => void;
  onLabel: (label: string) => void;
  onProp: (patch: Partial<IvrProps>) => void;
  onSetEntry: () => void;
  onRouteDigit: (digit: string, toId: string | null) => void;
  onSetNext: (toId: string | null) => void;
}) {
  if (!node) {
    return <Card title="מאפיינים"><p style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>בחר צומת בקנבס כדי לערוך.</p></Card>;
  }

  const otherNodes = graph.nodes.filter((n) => n.id !== node.id);
  const nextEdge = graph.edges.find((e) => e.from === node.id && !e.digit);
  const allTargets = [...targets.queues, ...targets.extensions];

  return (
    <Card title={`מאפייני: ${node.label}`}>
      <div className="stack" style={{ gap: 12 }}>
        <FormField label="כותרת">
          <TextInput value={node.label} onChange={(e) => onLabel(e.target.value)} />
        </FormField>

        {node.props?.entry ? (
          <StatusBadge tone="ok" size="sm" dot>צומת כניסה</StatusBadge>
        ) : (
          <button type="button" className="btn ghost" style={{ alignSelf: 'flex-start' }} onClick={onSetEntry}>
            סמן כצומת כניסה
          </button>
        )}

        {node.type === 'play' && (
          <PromptField
            label="הקלטת ההודעה"
            value={node.props?.prompt_id}
            prompts={prompts}
            onChange={(id) => onProp({ prompt_id: id })}
            onManage={onManagePrompts}
          />
        )}

        {node.type === 'menu' && (
          <PromptField
            label='הקלטת התפריט ("הקש 1 ל...")'
            value={node.props?.prompt_id}
            prompts={prompts}
            onChange={(id) => onProp({ prompt_id: id })}
            onManage={onManagePrompts}
          />
        )}

        {(node.type === 'queue' || node.type === 'transfer') && (
          <FormField label={node.type === 'queue' ? 'תור יעד' : 'יעד העברה'} required>
            <FormSelect
              value={node.props?.target ?? ''}
              onChange={(e) => onProp({ target: e.target.value })}
            >
              <option value="">בחר יעד…</option>
              {(node.type === 'queue' ? targets.queues : allTargets).map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </FormSelect>
          </FormField>
        )}

        {node.type === 'voicemail' && (
          <FormField label="תיבת דואר" hint="ריק = ברירת מחדל של התפריט">
            <TextInput value={node.props?.mailbox ?? ''} onChange={(e) => onProp({ mailbox: e.target.value })} dir="ltr" />
          </FormField>
        )}

        {node.type === 'menu' ? (
          <div>
            <strong style={{ fontSize: 'var(--t-sm)' }}>ניתוב ספרות</strong>
            <div className="stack" style={{ gap: 4, marginTop: 6 }}>
              {ROUTE_KEYS.map((d) => {
                const edge = graph.edges.find((e) => e.from === node.id && e.digit === d);
                return (
                  <div key={d} style={{ display: 'grid', gridTemplateColumns: d === 'timeout' ? '70px 1fr' : '24px 1fr', gap: 6, alignItems: 'center' }}>
                    <span dir={d === 'timeout' ? 'rtl' : 'ltr'} style={{ fontWeight: 700, textAlign: 'center', fontSize: d === 'timeout' ? 'var(--t-xs)' : undefined }} title={d === 'timeout' ? 'לאן ממשיכים כשלא הוקשה בחירה (אחרי 3 השמעות)' : undefined}>{d === 'timeout' ? 'אין הקשה' : d}</span>
                    <FormSelect value={edge?.to ?? ''} onChange={(e) => onRouteDigit(d, e.target.value || null)} style={{ padding: '6px 8px' }}>
                      <option value="">—</option>
                      {otherNodes.map((n) => (
                        <option key={n.id} value={n.id}>{n.label}</option>
                      ))}
                    </FormSelect>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <FormField label="המשך אל" hint="הצומת הבא לאחר ביצוע">
            <FormSelect value={nextEdge?.to ?? ''} onChange={(e) => onSetNext(e.target.value || null)}>
              <option value="">— סיום —</option>
              {otherNodes.map((n) => (
                <option key={n.id} value={n.id}>{n.label}</option>
              ))}
            </FormSelect>
          </FormField>
        )}
      </div>
    </Card>
  );
}
